import { SCHEDULE_CHAIN, etapasContratadas } from '../../constants/schedule';
import { BILLING_MODELS } from '../../constants';

/**
 * O QUE VAI EMBORA COM O SERVIÇO.
 *
 * A exclusão em cascata é a parte que ninguém imagina. No banco, apagar um
 * serviço apaga junto — por `on delete cascade` — as sessões de protocolo, OPU,
 * FIV, TE e prenhez, OS EMBRIÕES, as faturas e os textos de relatório de visita.
 * E zera, em outro serviço, o apontamento de quem fatura neste.
 *
 * "Tem certeza que deseja excluir?" não dizia nada disso. Quem confirmava
 * imaginava estar apagando uma linha de lista, e apagava o registro de dois meses
 * de bancada e as palhetas que estão no botijão.
 *
 * Esta lista é ESPECÍFICA DO SERVIÇO, e não um texto fixo: só entra o que aquele
 * serviço de fato tem. Aviso que lista o que não existe ensina a ignorar avisos —
 * e num aviso de exclusão isso é caro.
 */

const num = (v) => Number(v) || 0;

/**
 * As frases do que se perde, prontas para a tela.
 *
 * Em ordem de gravidade, e não de estrutura do banco: primeiro o que não se
 * reconstrói (embrião no botijão), depois o que se refaz com trabalho (os
 * lançamentos), depois o que é papel (fatura), por último o efeito colateral em
 * outro serviço — que é o único item que não é sobre este serviço, e o que ninguém
 * espera.
 */
export const oQueVaiComOServico = (service, { services = [], parcelas = [] } = {}) => {
  if (!service) return [];
  const itens = [];

  // ── O que estava guardado ───────────────────────────────────────────────
  const produzidos = num(service.lab_quantity);
  if (produzidos > 0) {
    itens.push(`${produzidos} ${produzidos === 1 ? 'embrião produzido' : 'embriões produzidos'}`
      + ', inclusive o que estiver congelado no botijão');
  }

  // ── O trabalho lançado ──────────────────────────────────────────────────
  // Só as etapas CONCLUÍDAS: uma etapa contratada e ainda por fazer não tem
  // lançamento a perder, e citá-la infla o aviso com o que não existe.
  const lancadas = etapasContratadas(service).filter(e => e.done);
  if (lancadas.length > 0) {
    itens.push(`Os lançamentos de ${lancadas.map(e => e.label).join(', ')}`);
  }

  // ── O dinheiro ──────────────────────────────────────────────────────────
  const faturas = new Set((parcelas || []).map(p => p?.invoice_id).filter(Boolean)).size;
  if (faturas > 0) {
    const pagas = (parcelas || []).filter(p => p?.paga_em).length;
    itens.push(`${faturas} fatura${faturas === 1 ? '' : 's'} emitida${faturas === 1 ? '' : 's'}`
      + (pagas > 0 ? `, com ${pagas} parcela${pagas === 1 ? '' : 's'} já paga${pagas === 1 ? '' : 's'}` : ''));
  } else if (service.billed) {
    // `billed` sem parcela em memória: a fatura existe e a leitura falhou, ou ela
    // foi emitida antes das parcelas existirem. Avisar sem número é melhor que
    // calar sobre uma fatura.
    itens.push('A fatura deste serviço');
  }

  // ── O efeito em OUTRO serviço ───────────────────────────────────────────
  // O `sem_cobranca` aponta para quem o fatura. Apagar o alvo deixa o outro
  // serviço sem para onde apontar, e ninguém espera que excluir um serviço mexa
  // em outro.
  const apontamPraEle = (services || []).filter(s =>
    s?.id !== service.id && s?.billing_service_id === service.id);
  if (apontamPraEle.length > 0) {
    const codigos = apontamPraEle.map(s => s.service_code || '(sem número)').join(', ');
    itens.push(`O apontamento de faturamento de ${codigos}, que passa a não faturar em lugar nenhum`);
  }

  return itens;
};

/**
 * Excluir este serviço é seguro de OFERECER?
 *
 * Não é uma trava, e é de propósito: quem decide é o laboratório, e cadastro
 * errado precisa poder sair mesmo depois de faturado. O que isto devolve é o
 * MOTIVO DE PESO, para a tela dizer antes e não depois.
 *
 * Dinheiro recebido é o único caso que muda a natureza da exclusão: apagar o
 * serviço apaga a fatura, e a fatura apagada leva o recebimento — o caixa muda
 * sozinho, sem lançamento nenhum que explique.
 */
export const pesoDaExclusao = (service, { parcelas = [] } = {}) => {
  const recebidas = (parcelas || []).filter(p => p?.paga_em);
  if (recebidas.length > 0) {
    return {
      grave: true,
      motivo: 'Este serviço tem parcela paga. Excluir apaga a fatura e o '
        + 'recebimento junto, e o caixa muda sem nenhum lançamento que explique.',
    };
  }

  if (service?.billing_model === BILLING_MODELS.SEM_COBRANCA) {
    return { grave: false, motivo: null };
  }

  // Etapa lançada é trabalho de campo que não volta. Não é grave como dinheiro
  // recebido, mas é o que a pessoa mais subestima.
  const concluidas = SCHEDULE_CHAIN.filter(e => service?.[e.doneField]).length;
  if (concluidas > 0) {
    return {
      grave: false,
      motivo: `Este serviço tem ${concluidas} etapa${concluidas === 1 ? '' : 's'} `
        + 'lançada, e o que foi registrado em campo não volta.',
    };
  }

  return { grave: false, motivo: null };
};
